/**
 * Unified API Response Handler
 * Handles consistent API response format across the application
 * Updated to work with the new standardized backend responseHelper format
 */

/**
 * Extract data from API response with fallback support
 * @param {Object} response - API response object
 * @param {string} dataPath - Optional specific data path (e.g., 'subscription', 'users')
 * @returns {*} Extracted data or null
 */
export const extractApiData = (response, dataPath = null) => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  // Handle new standardized response format: { success: true, message: "...", data: {...} }
  if (response.success && response.data !== undefined) {
    if (dataPath) {
      return response.data[dataPath] || null;
    }
    return response.data;
  }

  // Handle legacy formats for backward compatibility
  if (dataPath) {
    // Try multiple possible paths
    return response.data?.[dataPath] || response[dataPath] || response.data || null;
  }

  // Return the most likely data container
  return response.data || response;
};

/**
 * Extract pagination metadata from API response
 * @param {Object} response - API response object
 * @returns {Object|null} Pagination metadata or null
 */
export const extractPagination = response => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  // Handle unified response format
  if (response.success && response.pagination) {
    return response.pagination;
  }

  // Handle legacy formats
  return response.pagination || null;
};

/**
 * Check if API response indicates success
 * @param {Object} response - API response object
 * @returns {boolean} True if response indicates success
 */
export const isApiSuccess = response => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  return response.success === true;
};

/**
 * Extract error message from API response
 * @param {Object} response - API response object
 * @returns {string} Error message or default message
 */
export const extractApiError = response => {
  if (!response || typeof response !== 'object') {
    return 'Unknown error occurred';
  }

  // Handle new standardized error format
  if (response.success === false) {
    return response.message || response.error?.message || response.error || 'An error occurred';
  }

  // Handle legacy formats
  return response.message || response.error || 'An error occurred';
};

/**
 * Extract error details from API response (for validation errors, etc.)
 * @param {Object} response - API response object
 * @returns {Array|Object|null} Error details or null
 */
export const extractApiErrorDetails = response => {
  if (!response || typeof response !== 'object') {
    return null;
  }

  // Handle new standardized error format
  if (response.success === false) {
    return response.details || response.errors || null;
  }

  // Handle legacy formats
  return response.details || response.errors || null;
};

/**
 * Check if API response is a validation error
 * @param {Object} response - API response object
 * @returns {boolean} True if response is a validation error
 */
export const isValidationError = response => {
  if (!response || typeof response !== 'object') {
    return false;
  }

  return (
    response.success === false &&
    (response.message === 'Validation failed' || response.details || response.errors)
  );
};

/**
 * Handle API response with unified error handling
 * @param {Response} fetchResponse - Fetch response object
 * @param {string} dataPath - Optional specific data path
 * @returns {Promise<Object>} Processed response data
 */
export const handleApiResponse = async (fetchResponse, dataPath = null) => {
  // Check if response is ok first
  if (!fetchResponse.ok) {
    try {
      const errorResponse = await fetchResponse.json();
      return {
        success: false,
        error: extractApiError(errorResponse),
        message: extractApiError(errorResponse),
        data: null,
        pagination: null,
      };
    } catch (jsonError) {
      // If JSON parsing fails, try to get text
      const errorText = await fetchResponse.text().catch(() => 'Unknown error');
      return {
        success: false,
        error: errorText || `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`,
        message: errorText || `HTTP ${fetchResponse.status}: ${fetchResponse.statusText}`,
        data: null,
        pagination: null,
      };
    }
  }

  try {
    const response = await fetchResponse.json();

    // If response indicates failure, return error response instead of throwing
    if (!isApiSuccess(response)) {
      return {
        success: false,
        error: extractApiError(response),
        message: extractApiError(response),
        data: null,
        pagination: null,
      };
    }

    return {
      success: true,
      data: extractApiData(response, dataPath),
      pagination: extractPagination(response),
      message: response.message,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Unknown error',
      message: error.message || 'Unknown error',
      data: null,
      pagination: null,
    };
  }
};

/**
 * Create a standardized API request function
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @param {string} dataPath - Optional specific data path
 * @returns {Promise<Object>} Standardized response
 */
export const createApiRequest = async (endpoint, options = {}, dataPath = null) => {
  try {
    const response = await fetch(endpoint, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await handleApiResponse(response, dataPath);
  } catch (error) {
    return {
      success: false,
      error: error.message,
      data: null,
      pagination: null,
    };
  }
};

/**
 * Legacy compatibility function for existing code
 * @param {Object} response - API response object
 * @param {string} dataPath - Optional specific data path
 * @returns {*} Extracted data with legacy fallbacks
 */
export const getResponseData = (response, dataPath = null) => {
  if (!response) return null;

  // Try unified format first
  if (response.success && response.data !== undefined) {
    if (dataPath) {
      return response.data[dataPath] || null;
    }
    return response.data;
  }

  // Legacy fallbacks
  if (dataPath) {
    return response.data?.[dataPath] || response[dataPath] || response.data || null;
  }

  return response.data || response;
};

export default {
  extractApiData,
  extractPagination,
  isApiSuccess,
  extractApiError,
  extractApiErrorDetails,
  isValidationError,
  handleApiResponse,
  createApiRequest,
  getResponseData,
};
